import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { 
    Send, 
    Plus, 
    Trash2, 
    Heart, 
    BookOpen, 
    Sparkles,
    ArrowLeft,
    MessageCircle,
    Loader2,
    Volume2,
    Pause,
    Play,
    Key,
    FileCheck,
    AlertCircle,
    ExternalLink,
    Copy,
    Check,
    Brain,
    Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { chatApi, ttsApi, accessCodeApi } from '../api';
import { cn, formatRelativeTime } from '../lib/utils';
import { useLanguage } from '../context/LanguageContext';

// Unified chat - no more categories needed
const UNIFIED_CATEGORY = 'unified';

export function ChatPage() {
    const { category: urlCategory, conversationId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { t, language } = useLanguage();
    
    // Always use unified category - AI detects context automatically
    const [selectedCategory, setSelectedCategory] = useState(UNIFIED_CATEGORY);
    // REMOVED: conversations, currentConversation - privacy by design
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    
    // Access Code & Terms state
    const [accessCode, setAccessCode] = useState('');
    const [isCodeActivated, setIsCodeActivated] = useState(false);
    const [remainingQuestions, setRemainingQuestions] = useState(0);
    const [isValidatingCode, setIsValidatingCode] = useState(false);
    const [codeError, setCodeError] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);
    
    // Copy message state
    const [copiedMessageId, setCopiedMessageId] = useState(null);
    const scrollAreaRef = useRef(null);
    
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    
    // Load terms acceptance and access code from localStorage
    useEffect(() => {
        const savedTerms = localStorage.getItem('iulian_ai_terms_accepted');
        if (savedTerms === 'true') {
            setTermsAccepted(true);
        }
        
        const savedCode = localStorage.getItem('iulian_ai_access_code');
        if (savedCode) {
            setAccessCode(savedCode);
            // Validate saved code on load
            checkSavedCode(savedCode);
        }
    }, []);
    
    // Check if returning from terms page with acceptance
    useEffect(() => {
        if (location.state?.termsAccepted) {
            setTermsAccepted(true);
            // Clear the state to prevent re-triggering
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state]);
    
    const checkSavedCode = async (code) => {
        try {
            const result = await accessCodeApi.check(code);
            if (result.valid && result.remaining_questions > 0) {
                setIsCodeActivated(true);
                setRemainingQuestions(result.remaining_questions);
            }
        } catch (error) {
            console.error('Error checking saved code:', error);
        }
    };
    
    const handleActivateCode = async () => {
        if (!accessCode.trim()) return;
        
        setIsValidatingCode(true);
        setCodeError('');
        
        try {
            const result = await accessCodeApi.validate(accessCode.trim());
            
            if (result.valid && result.remaining_questions > 0) {
                setIsCodeActivated(true);
                setRemainingQuestions(result.remaining_questions);
                localStorage.setItem('iulian_ai_access_code', accessCode.trim());
                setCodeError('');
            } else if (result.remaining_questions === 0) {
                setCodeError(t('noQuestionsLeft'));
                setIsCodeActivated(false);
            } else {
                setCodeError(t('invalidCode'));
                setIsCodeActivated(false);
            }
        } catch (error) {
            console.error('Error validating code:', error);
            setCodeError(t('invalidCode'));
            setIsCodeActivated(false);
        } finally {
            setIsValidatingCode(false);
        }
    };
    
    const handleOpenTerms = () => {
        navigate('/terms', { 
            state: { 
                returnToChat: true, 
                returnPath: location.pathname 
            } 
        });
    };
    
    // Copy message function - copies Q&A pair
    const handleCopyMessage = async (msgIndex) => {
        // Find the user message (question) and assistant message (answer)
        const assistantMsg = messages[msgIndex];
        const userMsg = messages.slice(0, msgIndex).reverse().find(m => m.role === 'user');
        
        let textToCopy = '';
        if (userMsg) {
            textToCopy += `${language === 'ro' ? 'Întrebare' : 'Question'}:\n${userMsg.content}\n\n`;
        }
        textToCopy += `${language === 'ro' ? 'Răspuns' : 'Answer'}:\n${assistantMsg.content}`;
        
        try {
            await navigator.clipboard.writeText(textToCopy);
            setCopiedMessageId(assistantMsg.id || `msg-${msgIndex}`);
            setTimeout(() => setCopiedMessageId(null), 2000);
        } catch (error) {
            console.error('Failed to copy:', error);
        }
    };

    const handleDeleteMessage = (msgIndex) => {
        // Delete the assistant message and its corresponding user question
        const assistantMsg = messages[msgIndex];
        
        // Find the user message that precedes this assistant message
        let userMsgIndex = -1;
        for (let i = msgIndex - 1; i >= 0; i--) {
            if (messages[i].role === 'user') {
                userMsgIndex = i;
                break;
            }
        }
        
        // Remove both messages (user question + assistant answer)
        const newMessages = messages.filter((_, idx) => idx !== msgIndex && idx !== userMsgIndex);
        setMessages(newMessages);
        // Save to localStorage
        localStorage.setItem('iulian_ai_chat_history', JSON.stringify(newMessages));
    };

    // Load chat history from localStorage on mount
    useEffect(() => {
        const savedHistory = localStorage.getItem('iulian_ai_chat_history');
        if (savedHistory) {
            try {
                const parsedHistory = JSON.parse(savedHistory);
                if (Array.isArray(parsedHistory) && parsedHistory.length > 0) {
                    setMessages(parsedHistory);
                }
            } catch (e) {
                console.error('Error loading chat history:', e);
            }
        }
    }, []);

    // Navigate to chat directly without category selection
    useEffect(() => {
        // If user comes to /chat or /chat/:category, we handle it the same way
        // Category is now just for backward compatibility in URLs
        setSelectedCategory(UNIFIED_CATEGORY);
    }, [urlCategory]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Privacy by design: conversations exist only in browser session
    // When user closes the page, conversation is gone forever

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!inputMessage.trim() || isSending) return;
        if (!isCodeActivated || !termsAccepted) return;
        if (remainingQuestions <= 0) return;

        const userMessage = inputMessage.trim();
        setInputMessage('');
        setIsSending(true);

        const tempUserMsg = {
            id: `msg-${Date.now() - 1}`,
            role: 'user',
            content: userMessage,
            timestamp: new Date().toISOString()
        };
        
        // Add user message immediately
        const updatedMessages = [...messages, tempUserMsg];
        setMessages(updatedMessages);

        // Create placeholder for streaming response
        const assistantMsgId = `msg-${Date.now()}`;
        const assistantMsg = {
            id: assistantMsgId,
            role: 'assistant',
            content: '',
            timestamp: new Date().toISOString(),
            isStreaming: true
        };
        setMessages(prev => [...prev, assistantMsg]);

        try {
            // Decrement question counter
            const codeResult = await accessCodeApi.use(accessCode);
            setRemainingQuestions(codeResult.remaining_questions);
            
            // Stream message with real-time updates
            const sessionMessages = messages.map(m => ({ role: m.role, content: m.content }));
            let fullContent = '';
            
            // Use fetch for streaming
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: localStorage.getItem('iulian_ai_user_id') || 'anonymous',
                    category: UNIFIED_CATEGORY,
                    message: userMessage,
                    messages: sessionMessages
                })
            });
            
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';
                
                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));
                            if (data.error) {
                                throw new Error(data.error);
                            }
                            if (data.text) {
                                fullContent += data.text;
                                // Update the streaming message in real-time
                                setMessages(prev => prev.map(m => 
                                    m.id === assistantMsgId 
                                        ? { ...m, content: fullContent }
                                        : m
                                ));
                            }
                            if (data.done) {
                                // Mark streaming as complete
                                setMessages(prev => prev.map(m => 
                                    m.id === assistantMsgId 
                                        ? { ...m, isStreaming: false }
                                        : m
                                ));
                            }
                        } catch (e) {
                            // Skip invalid JSON
                        }
                    }
                }
            }
            
            // Save final messages to localStorage
            const finalMessages = [
                ...messages,
                tempUserMsg,
                { ...assistantMsg, content: fullContent, isStreaming: false }
            ];
            localStorage.setItem('iulian_ai_chat_history', JSON.stringify(finalMessages));

        } catch (error) {
            console.error('Error sending message:', error);
            // Remove the failed assistant message
            setMessages(prev => prev.filter(m => m.id !== assistantMsgId));
        } finally {
            setIsSending(false);
        }
    };

    // REMOVED: handleDeleteConversation - no conversations stored

    const startNewConversation = () => {
        setMessages([]);
        // Clear chat history from localStorage
        localStorage.removeItem('iulian_ai_chat_history');
        navigate('/chat');
    };

    // TTS - REMOVED for cost savings

    // UNIFIED CHAT - No category selection needed
    // AI automatically detects context from the user's message

    return (
        <div className="h-[calc(100vh-4rem-70px)] md:h-[calc(100vh-4rem)] flex overflow-hidden">
            {/* Main Chat Area - No sidebar needed (privacy by design) */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                {/* Chat Header */}
                <div className="h-16 border-b border-emerald-800/30 bg-[#0A0F0D]/50 backdrop-blur-sm flex items-center px-4 gap-4 shrink-0">
                    <button
                        onClick={() => navigate('/')}
                        data-testid="back-to-home"
                        className="p-2 hover:bg-emerald-500/10 rounded-lg text-emerald-400"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                        <Brain className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                        <h2 className="font-heading text-lg font-medium text-emerald-100">
                            {language === 'ro' ? 'Conversație Liberă' : 'Free Conversation'}
                        </h2>
                        <p className="text-sm text-emerald-400/80">
                            {language === 'ro' 
                                ? 'Scrie orice te preocupă - înțeleg contextul automat' 
                                : 'Write whatever concerns you - I understand context automatically'}
                        </p>
                    </div>
                    {messages.length > 0 && (
                        <button
                            onClick={() => {
                                if (window.confirm(language === 'ro' 
                                    ? 'Ești sigur că vrei să ștergi conversația? Aceasta nu poate fi recuperată.' 
                                    : 'Are you sure you want to delete this conversation? This cannot be undone.')) {
                                    startNewConversation();
                                }
                            }}
                            data-testid="new-conversation-btn"
                            className="ml-auto flex items-center gap-2 px-3 py-2 hover:bg-red-500/10 rounded-lg text-red-400 border border-red-500/30 hover:border-red-500/50 transition-all"
                            title={language === 'ro' ? 'Șterge conversația' : 'Delete conversation'}
                        >
                            <Trash2 className="w-4 h-4" />
                            <span className="text-sm hidden sm:inline">
                                {language === 'ro' ? 'Șterge' : 'Clear'}
                            </span>
                        </button>
                    )}
                </div>

                {/* Messages - scrollable area */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6" ref={scrollAreaRef}>
                    <div className="max-w-3xl mx-auto space-y-6 pb-4">
                        {isLoading ? (
                            <div className="flex justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                            </div>
                        ) : messages.length === 0 ? (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="text-center py-12"
                            >
                                <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                                    <Brain className="w-8 h-8 text-emerald-400" />
                                </div>
                                <h3 className="font-heading text-xl font-medium text-emerald-100 mb-2">
                                    {language === 'ro' ? 'Bine ai venit!' : 'Welcome!'}
                                </h3>
                                <p className="text-emerald-400/70 max-w-md mx-auto mb-6">
                                    {language === 'ro' 
                                        ? 'Scrie ce te preocupă - înțeleg automat dacă e vorba de anxietate, traume, emoții sau orice altceva.' 
                                        : 'Write what concerns you - I automatically understand if it\'s about anxiety, trauma, emotions or anything else.'}
                                </p>
                                <div className="flex flex-wrap justify-center gap-2 text-xs text-emerald-500/60">
                                    <span className="px-3 py-1 bg-emerald-900/30 rounded-full">anxietate</span>
                                    <span className="px-3 py-1 bg-emerald-900/30 rounded-full">traume</span>
                                    <span className="px-3 py-1 bg-emerald-900/30 rounded-full">emoții</span>
                                    <span className="px-3 py-1 bg-emerald-900/30 rounded-full">relații</span>
                                    <span className="px-3 py-1 bg-emerald-900/30 rounded-full">dezvoltare</span>
                                </div>
                            </motion.div>
                        ) : (
                            messages.map((msg, index) => {
                                const msgId = msg.id || `msg-${index}`;
                                
                                return (
                                    <motion.div
                                        key={msgId}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={cn(
                                            "flex flex-col",
                                            msg.role === 'user' ? 'items-end' : 'items-start'
                                        )}
                                    >
                                        {/* Educational Disclaimer Banner - BEFORE assistant response */}
                                        {msg.role === 'assistant' && msg.id !== 'temp-user' && (
                                            <div className="w-full max-w-[85%] md:max-w-[75%] mb-2 px-4 py-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                                                <p className="text-xs text-amber-400 font-medium text-center leading-relaxed">
                                                    ⚠️ {language === 'ro' 
                                                        ? 'INFORMAȚII EDUCAȚIONALE. NU CONSTITUIE RELAȚIE TERAPEUTICĂ SAU MEDICALĂ. UTILIZARE PE PROPRIA RĂSPUNDERE.'
                                                        : 'EDUCATIONAL INFO ONLY. NO THERAPEUTIC OR MEDICAL RELATIONSHIP ESTABLISHED. USE AT YOUR OWN RISK.'
                                                    } ⚠️
                                                </p>
                                                <p className="text-xs text-amber-300/80 text-center mt-1">
                                                    {language === 'ro'
                                                        ? 'Dacă nu te simți în siguranță, apelează la un specialist sau sună la numărul de urgență din țara ta.'
                                                        : 'If you don\'t feel safe, reach out to a specialist or call the emergency number in your country.'
                                                    }
                                                </p>
                                            </div>
                                        )}
                                        
                                        <div className={cn(
                                            "max-w-[85%] md:max-w-[75%]",
                                            msg.role === 'user' ? "chat-message-user" : "chat-message-assistant"
                                        )}>
                                            <div className="leading-relaxed prose prose-invert prose-sm max-w-none prose-p:my-2 prose-p:leading-relaxed">
                                                {msg.role === 'assistant' ? (
                                                    <ReactMarkdown>
                                                        {msg.content}
                                                    </ReactMarkdown>
                                                ) : (
                                                    <p className="whitespace-pre-wrap">{msg.content}</p>
                                                )}
                                                {/* Streaming cursor */}
                                                {msg.isStreaming && (
                                                    <span className="inline-block w-2 h-4 ml-1 bg-emerald-400 animate-pulse" />
                                                )}
                                            </div>
                                            
                                            {/* Copy and Delete buttons for assistant messages - only show when not streaming */}
                                            {msg.role === 'assistant' && !msg.isStreaming && msg.content && (
                                                <>
                                                    <div className="mt-3 pt-3 border-t border-emerald-800/30 flex items-center gap-2">
                                                        {/* Copy Button */}
                                                        <button
                                                            onClick={() => handleCopyMessage(index)}
                                                            data-testid={`copy-message-${msgId}`}
                                                            className={cn(
                                                                "flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                                                                copiedMessageId === msgId
                                                                    ? "bg-emerald-500 text-white"
                                                                    : "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
                                                            )}
                                                        >
                                                            {copiedMessageId === msgId ? (
                                                                <>
                                                                    <Check className="w-3.5 h-3.5" />
                                                                    {language === 'ro' ? 'Copiat!' : 'Copied!'}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Copy className="w-3.5 h-3.5" />
                                                                    {language === 'ro' ? 'Copiază' : 'Copy'}
                                                                </>
                                                            )}
                                                        </button>
                                                        
                                                        {/* Delete Button */}
                                                        <button
                                                            onClick={() => handleDeleteMessage(index)}
                                                            data-testid={`delete-message-${msgId}`}
                                                            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all bg-red-500/20 text-red-300 hover:bg-red-500/30"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                            {language === 'ro' ? 'Șterge' : 'Delete'}
                                                        </button>
                                                    </div>
                                                    
                                                    {/* Privacy Notice */}
                                                    <div className="mt-2 text-center">
                                                        <p className="text-xs text-slate-500 flex items-center justify-center gap-1.5">
                                                            <Lock className="w-3 h-3" />
                                                            {language === 'ro' 
                                                                ? 'Din motive de confidențialitate, nu memorez contextul răspunsului!'
                                                                : "For privacy reasons, I don't memorize the context of responses!"
                                                            }
                                                        </p>
                                                        <p className="text-xs text-amber-500/70 mt-1">
                                                            {language === 'ro' 
                                                                ? '⚠️ IulianAI poate uneori să greșească. Verifică informațiile importante.'
                                                                : '⚠️ IulianAI can sometimes make mistakes. Verify important information.'
                                                            }
                                                        </p>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </motion.div>
                                );
                            })
                        )}
                        
                        {/* Typing indicator - show only if sending but no streaming message exists */}
                        {isSending && !messages.some(m => m.isStreaming) && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="flex justify-start"
                            >
                                <div className="chat-message-assistant">
                                    <div className="typing-indicator">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                        
                        <div ref={messagesEndRef} />
                    </div>
                </div>

                {/* Input Area - With Access Code & Terms */}
                <div className="border-t border-emerald-800/30 bg-[#0A0F0D] shrink-0">
                    {/* Show setup sections ONLY if not fully activated */}
                    {!(isCodeActivated && termsAccepted) && (
                        <>
                            {/* Access Code Section */}
                            <div className="border-b border-emerald-800/20 p-3 md:p-4">
                                <div className="max-w-3xl mx-auto">
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        {/* Access Code Input */}
                                        <div className="flex-1">
                                            <label className="text-xs font-bold text-emerald-400 mb-1.5 flex items-center gap-2">
                                                <Key className="w-3.5 h-3.5" />
                                                {t('enterAccessCode')}
                                            </label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={accessCode}
                                                    onChange={(e) => {
                                                        setAccessCode(e.target.value);
                                                        setCodeError('');
                                                    }}
                                                    maxLength={10}
                                                    placeholder={t('accessCodePlaceholder')}
                                                    data-testid="access-code-input"
                                                    disabled={isCodeActivated}
                                                    className={cn(
                                                        "flex-1 bg-[#111916] border rounded-lg px-3 py-2 text-sm text-emerald-100 placeholder:text-emerald-700 outline-none transition-all font-mono",
                                                        isCodeActivated 
                                                            ? "border-emerald-500/50 bg-emerald-900/20" 
                                                            : "border-emerald-800/50 focus:border-emerald-500"
                                                    )}
                                                />
                                                <Button
                                                    type="button"
                                                    onClick={handleActivateCode}
                                                    disabled={!accessCode.trim() || isValidatingCode || isCodeActivated}
                                                    data-testid="activate-code-btn"
                                                    className={cn(
                                                        "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                                                        isCodeActivated
                                                            ? "bg-emerald-600 text-white"
                                                            : "bg-emerald-700 hover:bg-emerald-600 text-white disabled:opacity-50"
                                                    )}
                                                >
                                                    {isValidatingCode ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : isCodeActivated ? (
                                                        <>{t('codeActivated')}</>
                                                    ) : (
                                                        t('activate')
                                                    )}
                                                </Button>
                                            </div>
                                            {codeError && (
                                                <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                                                    <AlertCircle className="w-3 h-3" />
                                                    {codeError}
                                                </p>
                                            )}
                                        </div>
                                        
                                        {/* Questions Counter (large, during setup) */}
                                        {isCodeActivated && (
                                            <div className="flex items-end">
                                                <div className="bg-emerald-900/30 border border-emerald-700/50 rounded-lg px-4 py-2">
                                                    <span className="text-2xl font-bold text-emerald-400">{remainingQuestions}</span>
                                                    <span className="text-emerald-500 text-xs ml-1">{t('questionsRemaining')}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Terms & Conditions Section - READONLY checkbox */}
                            <div className="border-b border-emerald-800/20 p-3 md:p-4">
                                <div className="max-w-3xl mx-auto">
                                    <div className="flex items-center gap-3">
                                        {/* Readonly checkbox - only checked via Terms page */}
                                        <div 
                                            className={cn(
                                                "w-5 h-5 rounded border-2 flex items-center justify-center transition-all",
                                                termsAccepted 
                                                    ? "bg-emerald-500 border-emerald-500" 
                                                    : "bg-emerald-900/30 border-emerald-600"
                                            )}
                                            data-testid="terms-checkbox-display"
                                        >
                                            {termsAccepted && (
                                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </div>
                                        <span className="text-sm text-emerald-300 flex-1">
                                            {t('iAgreeToTerms')}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={handleOpenTerms}
                                            data-testid="open-terms-btn"
                                            className={cn(
                                                "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all",
                                                termsAccepted
                                                    ? "bg-emerald-600/50 text-emerald-200"
                                                    : "bg-emerald-800/50 hover:bg-emerald-700/50 text-emerald-300"
                                            )}
                                        >
                                            <FileCheck className="w-3.5 h-3.5" />
                                            {termsAccepted 
                                                ? (language === 'ro' ? 'Acceptat ✓' : 'Accepted ✓')
                                                : t('termsAndConditions')
                                            }
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                    
                    {/* Message Input */}
                    <div className="p-4 pb-16">
                        <form onSubmit={handleSendMessage} className="max-w-3xl mx-auto">
                            {/* Status Message - only show if not fully activated */}
                            {!(isCodeActivated && termsAccepted) && (
                                <div className="mb-3 p-3 bg-amber-900/20 border border-amber-600/30 rounded-xl">
                                    <p className="text-amber-300 text-sm flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4 shrink-0" />
                                        {!termsAccepted && !isCodeActivated 
                                            ? t('openTermsToEnable')
                                            : !termsAccepted 
                                                ? t('termsRequired')
                                                : t('enterCodeFirst')
                                        }
                                    </p>
                                </div>
                            )}
                            
                            <div className="flex gap-3 items-center">
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={inputMessage}
                                    onChange={(e) => setInputMessage(e.target.value)}
                                    placeholder={t('writeYourMessage')}
                                    data-testid="chat-input"
                                    className="flex-1 bg-[#111916] border border-emerald-800/50 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl px-5 py-3 text-emerald-100 placeholder:text-emerald-600 outline-none transition-all"
                                    disabled={isSending || !isCodeActivated || !termsAccepted}
                                />
                                
                                {/* Small counter - only show when fully activated */}
                                {isCodeActivated && termsAccepted && (
                                    <div className="shrink-0 px-3 py-2 bg-emerald-900/40 border border-emerald-700/40 rounded-lg">
                                        <span className="text-sm font-bold text-emerald-400">{remainingQuestions}</span>
                                        <span className="text-emerald-500/70 text-xs ml-1 hidden sm:inline">{language === 'ro' ? 'rămas' : 'left'}</span>
                                    </div>
                                )}
                                
                                <Button
                                    type="submit"
                                    data-testid="send-message-btn"
                                    disabled={!inputMessage.trim() || isSending || !isCodeActivated || !termsAccepted || remainingQuestions <= 0}
                                    className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl px-6 disabled:opacity-50 shadow-lg shadow-emerald-500/20"
                                >
                                    {isSending ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <Send className="w-5 h-5" />
                                    )}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ChatPage;
